import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import { useSelector } from 'react-redux';

import { useForm, useFormState, useActions } from 'utils/hooks';
import config from './config';
import Drawer, { DrawerHeader } from 'common/components/drawer';
import { DrawerState } from 'common/entities/drawer/DrawerTypes';
import { FormDrawer } from 'common/components/drawer';
import { FormBody } from 'common/components/drawer';
import { FormFooter } from 'common/components/drawer';
import Spinner from 'components/general/Spinner';

import { selectListOptionsFromStore } from 'store/lists/selectors';

import * as listActions from 'store/lists/actions';
import * as reportsActions from 'store/reports/actions';
import VisibilitySettings from 'components/visibility-settings';
import { getVisibleCompanies } from 'components/visibility-settings/helpers';

import { selectActiveOrganizations, selectDefaultStatus } from 'store/reports/selectors';

const Form = ({ drawer, active, refetchData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const { formState, collectValues, loadValues } = useForm(config, {});
  const { subStates } = useFormState(formState);

  const organizations = useSelector(state => selectListOptionsFromStore(state, 'organizations'));
  const activeOrganizations = useSelector(selectActiveOrganizations);
  const defaultStatus = useSelector(selectDefaultStatus);

  const [fetchListOptions, getReportsVisibility, updateReportsVisibility] = useActions([
    listActions.fetchListOptions,
    reportsActions.getReportsVisibility,
    reportsActions.updateReportsVisibility
  ]);

  const organizationsState = subStates('organizations');

  const initVisibility = useCallback(async () => {
    try {
      setLoading(true);

      await getReportsVisibility({ id: active?.id }).unwrap();
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  }, [getReportsVisibility, active?.id]);

  const onSumbit = async () => {
    const values = collectValues();

    const { organizations } = values;

    try {
      const activeOrganizations =
        (organizations || [])?.filter(organization => organization.active) || [];

      const allActive = activeOrganizations?.length === organizations?.length;
      const allHidden = !activeOrganizations?.length;

      const params = {
        id: active?.id,
        default: allActive ? 'show' : 'hide',
        organization_ids: allActive || allHidden ? [] : activeOrganizations?.map(o => o.id) || []
      };

      setIsSubmitting(true);

      await updateReportsVisibility(params).unwrap();
      drawer.close();
      refetchData();

      setIsSubmitting(false);
    } catch (e) {
      console.error(e);
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchListOptions('organizations');
  }, [fetchListOptions]);

  useEffect(() => {
    if (drawer.isOpen && active) {
      initVisibility();
    }
  }, [initVisibility, drawer.isOpen, active]);

  useEffect(() => {
    if (drawer.isOpen && active) {
      const parsedOrganizations =
        defaultStatus === 'hide' || activeOrganizations?.length
          ? getVisibleCompanies(organizations, activeOrganizations, defaultStatus)
          : organizations;

      loadValues({
        organizations: parsedOrganizations
      });
    }
  }, [drawer.isOpen, loadValues, organizations, activeOrganizations, active, defaultStatus]);

  return (
    <Drawer layout="table" {...drawer}>
      <DrawerHeader className="d-flex flex-column border-bottom pb-4">
        <span>Visibility Settings</span>
        <span className="fs-12 fw-bold">{active?.name || null}</span>
      </DrawerHeader>
      <FormDrawer>
        <FormBody>
          {loading ? (
            <Spinner />
          ) : (
            <VisibilitySettings
              text="Select in which companies, this report is active:"
              organizationsState={organizationsState}
            />
          )}
        </FormBody>

        <FormFooter>
          <Button color="cancel" className="px-0 py-1 me-4" onClick={drawer.close}>
            CANCEL
          </Button>
          <Button onClick={onSumbit} disabled={isSubmitting} color="primary" className="px-4">
            SAVE
          </Button>
        </FormFooter>
      </FormDrawer>
    </Drawer>
  );
};

Form.propTypes = {
  drawer: DrawerState,
  active: PropTypes.shape({
    id: PropTypes.number
  })
};

export default Form;
